import {
	AotCategory,
	AotCategoryType,
	logConstants,
	logConstantsType,
} from '../utils/SettingsLogger';
import { SettingsNativeBridgeBase } from './SettingsNativeBridgeBase';
import {
	GetSettingParams,
	GetSettingsParams,
	SetSettingParams,
	SetSettingsParams,
	InitializeParams,
	NativePlatform,
	GetSettingResponse,
	GetSettingsResponse,
	SetSettingResponse,
	SetSettingsResponse,
	InitializeResponse,
} from '../Contracts/index';
import { WriteTraceParams } from '../utils/SettingsLogger';
import { SettingsLogger } from '../utils/SettingsLogger';
import {
	isPlatformEntrypointSupported,
	getSupportedEntrypoints,
} from '../config/SettingsPlatformConfig';
import getSettingsNative from '../Contracts/settingsNative';

export class SettingsNativeBridge extends SettingsNativeBridgeBase {
	static _instance: SettingsNativeBridge;

	private constructor() {
		super();
	}

	static get Instance(): SettingsNativeBridge {
		return (
			SettingsNativeBridge._instance ||
			(SettingsNativeBridge._instance = new SettingsNativeBridge())
		);
	}

	/**
	 * Initialize bridge internal state (called automatically during bridge creation)
	 * This sets up the bridge without communicating with native platform
	 * @param initializeParams - Contains platform, settingsEntrypoint, and systemInfo
	 */
	private initializeBridge(initializeParams: InitializeParams): void {
		const { platform, settingsEntrypoint, systemInfo } = initializeParams;

		// Validate platform value
		if (!Object.values(NativePlatform).includes(platform)) {
			const error = `Invalid platform: ${platform}. Must be one of: ${Object.values(
				NativePlatform
			).join(', ')}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				error,
				AotCategory.Error,
				error
			);
			throw new Error(error);
		}

		// Validate platform and entrypoint combination
		if (!isPlatformEntrypointSupported(platform, settingsEntrypoint)) {
			const supportedEntrypoints = getSupportedEntrypoints(platform);
			const error = `Platform '${platform}' does not support entrypoint '${settingsEntrypoint}'. Supported entrypoints: ${supportedEntrypoints.join(
				', '
			)}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				error,
				AotCategory.Error,
				error
			);
			throw new Error(error);
		}

		this._platform = platform;
		this._entrypoint = settingsEntrypoint;
		this._systemInfo = systemInfo;
		this._isInitialized = true;

		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Settings bridge initialized - Platform: ${platform}, Entry: ${settingsEntrypoint}, Version: ${systemInfo.version}`,
			AotCategory.Info,
			`Settings bridge initialized - Platform: ${platform}, Entry: ${settingsEntrypoint}, Version: ${systemInfo.version}`
		);

		// Notify all registered callbacks
		this._onInitializedCallbacks.forEach((callback) => callback(initializeParams));
	}

	/**
	 * Send initialize completion response to native platform (Web → Native)
	 * This method calls settingsNative.onInitializeComplete to confirm initialization
	 * @param id - Request ID from the initialize call
	 * @param response - InitializeResponse to send back to native
	 */
	onInitializeComplete(id: string, response: InitializeResponse): void {
		try {
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Initialize complete (status: ${response.status})`,
				AotCategory.Info,
				`Initialize complete (status: ${response.status})`
			);

			// Send completion response to native
			getSettingsNative().onInitializeComplete(id, JSON.stringify(response));
		} catch (error) {
			const errorMsg = `Failed to send initialize complete: ${error}`;
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				errorMsg,
				AotCategory.Error,
				errorMsg
			);
		}
	}

	/**
	 * Handle a refresh request for a specific setting (Native → Web callback)
	 * Dispatches to all registered onRefreshSetting subscribers.
	 * @param settingKey - The key of the setting to refresh
	 */
	refreshSetting(settingKey: string): void {
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Received refreshSetting for key: ${settingKey}`,
			AotCategory.Info,
			`Received refreshSetting for key: ${settingKey}`
		);
		this._onRefreshSettingCallbacks.forEach((callback) => callback(settingKey));
	}

	/**
	 * Legacy initialize method (for backwards compatibility)
	 */
	async initialize(initializeParams: InitializeParams): Promise<InitializeResponse> {
		// For backward compatibility, just do bridge initialization

		this.initializeBridge(initializeParams);
		return {
			status: 0,
			data: {
				platform: initializeParams.platform,
				settingsEntrypoint: initializeParams.settingsEntrypoint,
				systemInfo: initializeParams.systemInfo,
			},
		};
	}

	async getSetting(params: GetSettingParams): Promise<GetSettingResponse> {
		return new Promise<GetSettingResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Invoking native getSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`Invoking native getSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			getSettingsNative().getSetting(reqId, JSON.stringify(params));
		});
	}

	async getSettings(params: GetSettingsParams): Promise<GetSettingsResponse> {
		return new Promise<GetSettingsResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Invoking native getSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`Invoking native getSettings with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			getSettingsNative().getSettings(reqId, JSON.stringify(params));
		});
	}

	async setSetting(params: SetSettingParams): Promise<SetSettingResponse> {
		return new Promise<SetSettingResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Invoking native setSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`,
				AotCategory.Info,
				`Invoking native setSetting with id: ${reqId} and params: ${JSON.stringify(
					params
				)}`
			);
			getSettingsNative().setSetting(reqId, JSON.stringify(params));
		});
	}

	async setSettings(params: SetSettingsParams): Promise<SetSettingsResponse> {
		return new Promise<SetSettingsResponse>((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Invoking native setSettings with id: ${reqId} and params: ${
					typeof params === 'object' ? JSON.stringify(params) : params
				}`
			);
			getSettingsNative().setSettings(reqId, JSON.stringify(params));
		});
	}

	writeTrace(
		level: logConstantsType,
		message: string,
		aotCategory?: AotCategoryType,
		aoTrace?: string
	): void {
		const params: WriteTraceParams = { level, aotCategory, message, aotMessage: aoTrace };
		getSettingsNative().writeTrace(JSON.stringify(params));
	}

	settingsReady(): void {
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			'Settings ready',
			AotCategory.Info,
			'Settings ready'
		);
		getSettingsNative().settingsReady();
	}

	onGetSettingComplete(id: string, response: GetSettingResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Invoking native onGetSettingComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`Invoking native onGetSettingComplete with id: ${id} and response: ${responseStr}`
		);
		getSettingsNative().onGetSettingComplete(id, JSON.stringify(response));
	}

	onGetSettingsComplete(id: string, response: GetSettingsResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Invoking native onGetSettingsComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`Invoking native onGetSettingsComplete with id: ${id} and response: ${responseStr}`
		);
		getSettingsNative().onGetSettingsComplete(id, JSON.stringify(response));
	}

	onSetSettingComplete(id: string, response: SetSettingResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Invoking native onSetSettingComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`Invoking native onSetSettingComplete with id: ${id} and response: ${responseStr}`
		);
		getSettingsNative().onSetSettingComplete(id, JSON.stringify(response));
	}

	onSetSettingsComplete(id: string, response: SetSettingsResponse): void {
		const responseStr = JSON.stringify(response);
		SettingsLogger.getInstance().write(
			logConstants.INFO,
			`Invoking native onSetSettingsComplete with id: ${id} and response: ${responseStr}`,
			AotCategory.Info,
			`Invoking native onSetSettingsComplete with id: ${id} and response: ${responseStr}`
		);
		getSettingsNative().onSetSettingsComplete(id, JSON.stringify(response));
	}
}
